import React from 'react';
import { Check, RefreshCcw, Save } from 'lucide-react';

import PriceDistributionView from '../PriceDistributionView';
import { formatConfidencePercent } from '../../utils/formatters';

const getConditionBadgeClass = (condition) => {
  if (condition === 'New') return 'bg-positive/10 text-positive';
  if (condition === 'Damaged' || condition === 'For Parts') return 'bg-red-500/10 text-red-600 dark:text-red-400';
  if (condition === 'Unknown') return 'bg-surface-2 text-muted';
  return 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400';
};

const getPriceSourceBadgeClass = (priceSource) => {
  if (priceSource === 'ebay_rag') return 'bg-brand/10 text-brand';
  if (priceSource === 'image') return 'bg-amber-500/10 text-amber-600 dark:text-amber-400';
  return 'bg-surface-2 text-muted';
};

const getPriceSourceLabel = (priceSource) => {
  if (priceSource === 'ebay_rag') return 'eBay RAG';
  if (priceSource === 'image') return 'Image';
  return 'Model Knowledge';
};

// Small in-input save affordance shared by the name and price fields:
// spinner while saving, save icon when dirty, check when persisted.
const SaveAdornment = ({ isDirty, isUpdating, onClick, savedTitle, dirtyTitle }) => (
  <button
    onClick={onClick}
    disabled={!isDirty || isUpdating}
    className={`absolute right-1 p-1 rounded transition-all ${
      isUpdating
        ? 'text-muted'
        : isDirty
          ? 'text-brand hover:bg-brand/10'
          : 'text-positive cursor-default'
    }`}
    title={isDirty ? dirtyTitle : savedTitle}
  >
    {isUpdating ? (
      <RefreshCcw size={14} className="animate-spin" />
    ) : isDirty ? (
      <Save size={14} />
    ) : (
      <Check size={14} />
    )}
  </button>
);

const AnalysisItemsList = ({
  items,
  currentImageIndex,
  originalPrices,
  originalNames,
  updatingId,
  updatingNameId,
  onManualNameChange,
  onUpdateItemName,
  onPriceChange,
  onUpdatePrice,
  isAdmin = false,
}) => {
  return (
    <div className="w-full md:w-1/2 space-y-3 sm:space-y-4">
      {(items || []).map((item, idx) => {
        const isDirty = item.id
          && originalPrices[item.id] !== undefined
          && parseFloat(originalPrices[item.id]) !== parseFloat(item.actual_price);
        const isUpdating = updatingId === item.id;
        const isNameDirty = item.id
          && originalNames[item.id] !== undefined
          && originalNames[item.id] !== (item.manual_name || '');
        const isNameUpdating = updatingNameId === item.id;
        const condition = item?.condition || 'Unknown';
        const priceSource = item?.price_source || 'model_knowledge';
        const title = item.bounding_boxes
          && Array.isArray(item.bounding_boxes)
          && item.bounding_boxes[currentImageIndex]
          && item.bounding_boxes[currentImageIndex].label
          ? item.bounding_boxes[currentImageIndex].label
          : item.item;

        return (
          <div key={idx} className="bg-surface-2 dark:bg-surface p-3 sm:p-4 rounded-2xl border border-hairline shadow-sm hover:border-brand transition-colors">
            {/* Top row: title + condition/source (left) · estimated value (right) */}
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <strong className="block text-ink text-base leading-snug font-semibold">{title}</strong>
                {/* Condition + AI-source badges are internal detail — admins only */}
                {isAdmin && (
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold ${getConditionBadgeClass(condition)}`}>
                      {condition}
                    </span>
                    <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold ${getPriceSourceBadgeClass(priceSource)}`}>
                      {getPriceSourceLabel(priceSource)}
                    </span>
                  </div>
                )}
              </div>
              <div className="shrink-0 text-right">
                <div className="text-[22px] font-semibold text-positive leading-6 tabular-nums">${item.estimated_value}</div>
                <div className="mt-0.5 text-xs text-muted">Estimated Value</div>
              </div>
            </div>

            {/* Manual name override — admin / superuser only */}
            {isAdmin && (
            <div className="mt-2 flex items-center gap-2">
              <span className="shrink-0 text-[11px] uppercase tracking-wide text-muted">Name</span>
              <div className="relative flex flex-1 items-center">
                <input
                  type="text"
                  placeholder="Add custom name…"
                  className={`w-full rounded border px-2 py-0.5 pr-8 text-sm text-ink focus:outline-none focus:ring-1 transition-all ${
                    isNameDirty
                      ? 'border-brand focus:ring-brand bg-brand/5'
                      : 'border-hairline focus:ring-positive bg-ink/[0.04]'
                  }`}
                  value={item.manual_name || ''}
                  onChange={(e) => onManualNameChange(item.id, e.target.value)}
                />
                {item.id && (
                  <SaveAdornment
                    isDirty={isNameDirty}
                    isUpdating={isNameUpdating}
                    onClick={() => onUpdateItemName(item.id, item.manual_name || '')}
                    savedTitle="Name Saved"
                    dirtyTitle="Save Name"
                  />
                )}
              </div>
            </div>
            )}

            {/* Price range / distribution (kept feature, compact) */}
            <PriceDistributionView
              compact
              allowBreakdown={isAdmin}
              className="mt-2"
              rangeValue={item?.estimated_value_range}
              distributionValue={item?.price_estimate_distribution}
            />

            {/* Bottom row: actual price (left) · confidence (right) — Figma "Your bid" row */}
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="text-sm text-ink/70">Your bid</span>
                <div className="relative flex items-center">
                  <span className="absolute left-2 text-muted text-sm">$</span>
                  <input
                    type="number"
                    placeholder="0.00"
                    className={`w-24 pl-5 pr-8 py-1 border rounded text-sm text-ink focus:outline-none focus:ring-1 transition-all ${
                      isDirty
                        ? 'border-brand focus:ring-brand bg-brand/5'
                        : 'border-hairline focus:ring-positive bg-ink/[0.04]'
                    }`}
                    value={item.actual_price}
                    onChange={(e) => onPriceChange(item.id, e.target.value)}
                    step="0.01"
                  />
                  {item.id && (
                    <SaveAdornment
                      isDirty={isDirty}
                      isUpdating={isUpdating}
                      onClick={() => onUpdatePrice(item.id, item.actual_price)}
                      savedTitle="Price Saved"
                      dirtyTitle="Save Price"
                    />
                  )}
                </div>
              </div>
              <span className="inline-flex items-center rounded-md bg-positive/20 px-2.5 py-0.5 text-xs font-semibold text-positive">
                Confidence: {formatConfidencePercent(item?.confidence_score)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AnalysisItemsList;
